import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays, FileDown, Printer, MessageCircle, Loader2, ChevronDown, ChevronRight, ShieldCheck, ScanFace } from "lucide-react";

const LOGO = "https://customer-assets-4nw71qhi.emergentagent.net/job_academia-control-3/artifacts/hiofilsr_LOGO%20AMERICANFIT.png";
const todaySTM = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Santarem" });
const lastDay = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();
const pad = (n) => String(n).padStart(2, "0");
const br = (iso) => (iso ? iso.split("-").reverse().join("/") : "");
const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

export function presencaRange(mode, f) {
  if (mode === "dia") return f.dia ? [f.dia, f.dia] : null;
  if (mode === "mes") {
    if (!f.mes) return null;
    const [y, m] = f.mes.split("-").map(Number);
    return [`${f.mes}-01`, `${f.mes}-${pad(lastDay(y, m))}`];
  }
  if (mode === "ano") return f.ano ? [`${f.ano}-01-01`, `${f.ano}-12-31`] : null;
  if (mode === "periodo") return f.ini && f.fim ? [f.ini, f.fim] : null;
  return ["", ""];
}

const SituacaoBadge = ({ it }) => it.passagem_fisica ? (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-700"><ShieldCheck className="w-3 h-3"/>Passagem física comprovada</span>
) : (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-sky-100 text-sky-700"><ScanFace className="w-3 h-3"/>{it.origem === "Intelbras" ? "Autorização facial" : "Acesso registrado"}<span className="font-normal opacity-80">· giro não comprovado</span></span>
);

function PresencaRow({ it, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setOpen(!open)} data-testid={`presenca-row-${idx}`}>
        <td className="px-3 py-2.5 text-slate-400">{open ? <ChevronDown className="w-4 h-4"/> : <ChevronRight className="w-4 h-4"/>}</td>
        <td className="px-3 py-2.5 font-semibold text-slate-900" data-testid={`presenca-data-${idx}`}>{it.data}</td>
        <td className="px-3 py-2.5 font-mono" data-testid={`presenca-hora-${idx}`}>{it.hora}</td>
        <td className="px-3 py-2.5" data-testid={`presenca-unidade-${idx}`}>{it.unidade}</td>
        <td className="px-3 py-2.5" data-testid={`presenca-tipo-${idx}`}>{it.tipo}</td>
        <td className="px-3 py-2.5" data-testid={`presenca-origem-${idx}`}>{it.origem}</td>
        <td className="px-3 py-2.5" data-testid={`presenca-situacao-${idx}`}><SituacaoBadge it={it}/></td>
      </tr>
      {open && (
        <tr className="bg-slate-50" data-testid={`presenca-detalhes-${idx}`}>
          <td/>
          <td colSpan={6} className="px-3 pb-3 pt-1 text-xs text-slate-600 space-y-0.5">
            <div><span className="text-slate-400 uppercase tracking-wider mr-2">ID do evento</span><span className="font-mono break-all" data-testid={`presenca-event-id-${idx}`}>{it.event_id}</span></div>
            <div><span className="text-slate-400 uppercase tracking-wider mr-2">Registro</span><span className="font-mono">{it.registro_id}</span></div>
            {it.registro_leitor != null && <div><span className="text-slate-400 uppercase tracking-wider mr-2">Nº no leitor</span><span className="font-mono">{it.registro_leitor}</span></div>}
            <div><span className="text-slate-400 uppercase tracking-wider mr-2">UTC</span><span className="font-mono">{it.timestamp_utc}</span></div>
          </td>
        </tr>
      )}
    </>
  );
}

function PresencaFilters({ mode, setMode, f, setF, unit, setUnit, units }) {
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const years = Array.from({ length: 6 }, (_, i) => String(new Date().getFullYear() - i));
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Filtro</div>
        <Select value={mode} onValueChange={setMode}>
          <SelectTrigger className="w-40" data-testid="presencas-filtro-modo"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="mes">Mês</SelectItem><SelectItem value="dia">Dia</SelectItem><SelectItem value="ano">Ano</SelectItem>
            <SelectItem value="periodo">Período personalizado</SelectItem><SelectItem value="tudo">Todo o histórico</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {mode === "dia" && <Input type="date" value={f.dia} onChange={set("dia")} className="w-44" data-testid="presencas-filtro-dia"/>}
      {mode === "mes" && <Input type="month" value={f.mes} onChange={set("mes")} className="w-44" data-testid="presencas-filtro-mes"/>}
      {mode === "ano" && (
        <Select value={f.ano} onValueChange={(v) => setF({ ...f, ano: v })}>
          <SelectTrigger className="w-32" data-testid="presencas-filtro-ano"><SelectValue/></SelectTrigger>
          <SelectContent>{years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {mode === "periodo" && (
        <>
          <Input type="date" value={f.ini} onChange={set("ini")} className="w-44" data-testid="presencas-filtro-inicio"/>
          <span className="pb-2 text-slate-400 text-sm">até</span>
          <Input type="date" value={f.fim} onChange={set("fim")} className="w-44" data-testid="presencas-filtro-fim"/>
        </>
      )}
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Unidade</div>
        <Select value={unit} onValueChange={setUnit}>
          <SelectTrigger className="w-56" data-testid="presencas-filtro-unidade"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as unidades</SelectItem>
            {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function periodoLabel(r) {
  const p = r.periodo;
  if (p.inicio && p.fim) return p.inicio === p.fim ? br(p.inicio) : `${br(p.inicio)} a ${br(p.fim)}`;
  return "Todo o histórico";
}

function printHtml(r) {
  const rows = r.itens.map((i) => `<tr><td>${i.data}</td><td>${i.hora}</td><td>${esc(i.unidade)}</td><td>${esc(i.tipo)}</td><td>${esc(i.origem)}</td><td>${esc(i.situacao)}</td></tr>`).join("")
    || `<tr><td colspan="6" style="text-align:center;color:#64748B">Nenhum registro de presença no período selecionado.</td></tr>`;
  return `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Presenças — ${esc(r.aluno.nome)}</title><style>
    @page { size: A4; margin: 14mm; } * { box-sizing: border-box; }
    body { font-family: -apple-system,'Segoe UI',system-ui,sans-serif; color:#0F172A; margin:0; }
    .head { display:flex; gap:18px; align-items:center; border-bottom:3px solid #E11D48; padding-bottom:12px; margin-bottom:16px; }
    .head img { height:64px; } h1 { font-size:20px; margin:0 0 4px; text-transform:uppercase; }
    .meta { font-size:12px; color:#475569; line-height:1.6; } table { width:100%; border-collapse:collapse; font-size:11px; }
    th { background:#0F172A; color:#fff; text-align:left; padding:7px 8px; font-size:10px; text-transform:uppercase; letter-spacing:.5px; }
    td { padding:6px 8px; border-bottom:1px solid #E2E8F0; } tr:nth-child(even) td { background:#F8FAFC; } thead { display:table-header-group; }
    tr { page-break-inside:avoid; } .nota { font-size:10px; color:#64748B; margin-top:12px; } .rod { font-size:9px; color:#94A3B8; margin-top:18px; }
  </style></head><body>
    <div class="head"><img src="${LOGO}" alt="American Fit"/><div><h1>Relatório de Presenças</h1><div class="meta">
      <b>Aluno:</b> ${esc(r.aluno.nome)}<br/><b>Período:</b> ${periodoLabel(r)} · <b>Unidade:</b> ${esc(r.unidade_filtro || "Todas as unidades")}<br/>
      <b>Registros:</b> ${r.resumo.registros} · <b>Dias com entrada:</b> ${r.resumo.dias_distintos}</div></div></div>
    <table><thead><tr><th>Data</th><th>Horário</th><th>Unidade</th><th>Tipo</th><th>Origem</th><th>Situação</th></tr></thead><tbody>${rows}</tbody></table>
    <div class="nota">Horários no fuso America/Santarem. "Autorização facial" indica reconhecimento e liberação pelo leitor Intelbras; não comprova o giro físico da catraca.</div>
    <div class="rod">American Fit · Gerado em ${new Date().toLocaleString("pt-BR", { timeZone: "America/Santarem" })}</div>
    <script>const i=document.querySelector('img');const go=()=>setTimeout(()=>window.print(),300);if(i.complete)go();else{i.onload=go;i.onerror=go;}</script>
  </body></html>`;
}

function waPhone(p) {
  const d = String(p || "").replace(/\D/g, "");
  if (!d) return "";
  return d.length <= 11 ? `55${d}` : d;
}

export default function StudentPresencas({ studentId }) {
  const now = todaySTM();
  const [mode, setMode] = useState("mes");
  const [f, setF] = useState({ dia: now, mes: now.slice(0, 7), ano: now.slice(0, 4), ini: `${now.slice(0, 7)}-01`, fim: now });
  const [unit, setUnit] = useState("all");
  const [units, setUnits] = useState([]);
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const range = useMemo(() => presencaRange(mode, f), [mode, f]);
  const params = range ? { inicio: range[0], fim: range[1], unit_id: unit === "all" ? "" : unit } : null;

  useEffect(() => { api.get("/units").then(({ data }) => setUnits(data || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!params) { setR(null); return; }
    setLoading(true);
    api.get(`/students/${studentId}/presencas`, { params })
      .then(({ data }) => setR(data))
      .catch((e) => { setR(null); toast.error(e.response?.data?.detail || "Falha ao carregar presenças"); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, range?.[0], range?.[1], unit]);

  const getPdf = async () => {
    const { data } = await api.get(`/students/${studentId}/presencas/pdf`, { params, responseType: "blob" });
    const nome = (r?.aluno?.nome || "aluno").normalize("NFKD").replace(/[^\w]+/g, "_");
    return new File([data], `Presencas_${nome}.pdf`, { type: "application/pdf" });
  };
  const download = (file) => {
    const url = URL.createObjectURL(file);
    const a = document.createElement("a"); a.href = url; a.download = file.name; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };
  const run = (k, fn) => async () => {
    setBusy(k);
    try { await fn(); } catch (e) { if (e?.name !== "AbortError") toast.error(e.response?.data?.detail || "Não foi possível gerar o relatório"); }
    finally { setBusy(""); }
  };
  const onPdf = run("pdf", async () => { download(await getPdf()); toast.success("PDF gerado"); });
  const onPrint = () => {
    if (!r) return;
    const w = window.open("", "_blank", "width=900,height=800");
    if (!w) return toast.error("Popup bloqueado pelo navegador");
    w.document.write(printHtml(r)); w.document.close();
  };
  const onWhats = run("wa", async () => {
    const file = await getPdf();
    const text = `Relatório de presenças American Fit — ${r?.aluno?.nome} (${periodoLabel(r)})`;
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: "Relatório de Presenças", text });
      return;
    }
    download(file);
    const ph = waPhone(r?.aluno?.telefone);
    window.open(`https://wa.me/${ph}?text=${encodeURIComponent(text + " — segue o PDF em anexo.")}`, "_blank", "noopener");
    toast.info("PDF baixado. Anexe o arquivo na conversa do WhatsApp que foi aberta — nada é enviado automaticamente.", { duration: 7000 });
  });

  return (
    <div className="space-y-4" data-testid="presencas-tab">
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2"><CalendarDays className="w-5 h-5 text-rose-600"/>Presenças</h3>
            <p className="text-xs text-slate-500 mt-0.5">Todas as unidades da rede · horários em America/Santarem · autorização facial não comprova o giro da catraca</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={onPdf} disabled={!r || !!busy} data-testid="presencas-pdf-btn">{busy === "pdf" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/> : <FileDown className="w-4 h-4 mr-1.5"/>}PDF</Button>
            <Button size="sm" variant="outline" onClick={onPrint} disabled={!r} data-testid="presencas-print-btn"><Printer className="w-4 h-4 mr-1.5"/>Imprimir</Button>
            <Button size="sm" onClick={onWhats} disabled={!r || !!busy} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="presencas-whatsapp-btn">{busy === "wa" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/> : <MessageCircle className="w-4 h-4 mr-1.5"/>}WhatsApp</Button>
          </div>
        </div>
        <PresencaFilters mode={mode} setMode={setMode} f={f} setF={setF} unit={unit} setUnit={setUnit} units={units}/>
        {!range && <p className="text-xs text-amber-600" data-testid="presencas-filtro-incompleto">Preencha o filtro para consultar.</p>}
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3"><div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Registros</div><div className="text-2xl font-black text-slate-900" data-testid="presencas-resumo-registros">{r?.resumo?.registros ?? "—"}</div></div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3"><div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Dias com entrada</div><div className="text-2xl font-black text-slate-900" data-testid="presencas-resumo-dias">{r?.resumo?.dias_distintos ?? "—"}</div></div>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center text-slate-400" data-testid="presencas-loading"><Loader2 className="w-6 h-6 animate-spin mx-auto"/></div>
        ) : !r?.itens?.length ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="presencas-vazio">Nenhum registro de presença no período selecionado.</div>
        ) : (
          <table className="w-full text-sm min-w-[760px]" data-testid="presencas-tabela">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="w-8"/><th className="px-3 py-2 text-left">Data</th><th className="px-3 py-2 text-left">Horário</th><th className="px-3 py-2 text-left">Unidade</th><th className="px-3 py-2 text-left">Tipo</th><th className="px-3 py-2 text-left">Origem</th><th className="px-3 py-2 text-left">Situação</th></tr>
            </thead>
            <tbody>{r.itens.map((it, i) => <PresencaRow key={it.event_id} it={it} idx={i}/>)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
